if (!customElements.get('unilar-scheduled-visibility')) {
  const SCHEDULED_VISIBILITY_MAX_TIMEOUT = 2147483647;

  class UnilarScheduledVisibility extends HTMLElement {
    constructor() {
      super();

      this.timeoutId = null;
      this.visible = null;
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    static get observedAttributes() {
      return ['data-enabled', 'data-start-at', 'data-end-at', 'data-start-date', 'data-end-date', 'data-design-mode'];
    }

    connectedCallback() {
      this.update();
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    disconnectedCallback() {
      this.clearTimer();
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    attributeChangedCallback() {
      if (!this.isConnected) return;

      this.update();
    }

    handleVisibilityChange() {
      if (!document.hidden) {
        this.update();
      }
    }

    update() {
      this.clearTimer();

      const state = this.getState();
      this.setVisibility(state.visible);
      this.scheduleNextUpdate(state.nextUpdateAt);
    }

    getState() {
      const isDesignMode = this.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode);

      if (isDesignMode) {
        return { visible: true, nextUpdateAt: null };
      }

      if (this.dataset.enabled === 'false') {
        return { visible: false, nextUpdateAt: null };
      }

      const now = Date.now();
      const startAt = this.getTimestamp('startAt', 'startDate');
      const endAt = this.getTimestamp('endAt', 'endDate');

      if (startAt && now < startAt) {
        return { visible: false, nextUpdateAt: startAt };
      }

      if (endAt && now >= endAt) {
        return { visible: false, nextUpdateAt: null };
      }

      return { visible: true, nextUpdateAt: endAt || null };
    }

    getTimestamp(timestampKey, dateKey) {
      const timestamp = this.normalizeTimestamp(this.dataset[timestampKey]);

      if (timestamp) {
        return timestamp;
      }

      return this.parseDate(this.dataset[dateKey]);
    }

    normalizeTimestamp(value) {
      if (!value || !String(value).trim()) {
        return null;
      }

      const parsed = Number(value);

      if (!Number.isFinite(parsed) || parsed <= 0) {
        return null;
      }

      return parsed < 100000000000 ? parsed * 1000 : parsed;
    }

    parseDate(value) {
      if (!value || !String(value).trim()) {
        return null;
      }

      const parsed = new Date(String(value).trim().replace(' ', 'T')).getTime();

      return Number.isNaN(parsed) ? null : parsed;
    }

    setVisibility(visible) {
      if (this.visible === visible) {
        return;
      }

      this.visible = visible;
      this.toggleAttribute('hidden', !visible);
      this.setAttribute('aria-hidden', String(!visible));
      this.dataset.scheduledActive = String(visible);

      this.dispatchEvent(
        new CustomEvent('unilar:scheduled-visibility-change', {
          bubbles: true,
          detail: { visible },
        })
      );
    }

    scheduleNextUpdate(nextUpdateAt) {
      if (!nextUpdateAt) {
        return;
      }

      const delay = Math.max(0, nextUpdateAt - Date.now());

      this.timeoutId = window.setTimeout(() => {
        this.update();
      }, Math.min(delay, SCHEDULED_VISIBILITY_MAX_TIMEOUT));
    }

    clearTimer() {
      if (!this.timeoutId) {
        return;
      }

      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  customElements.define('unilar-scheduled-visibility', UnilarScheduledVisibility);
}
